import React, { useMemo, useState } from 'react';
import { Paper, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { VisibilityRounded, AddRounded, ToggleOnRounded, ToggleOffRounded } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { FilterBar, Field, SearchInput, SelectInput, Spacer, ResetButton } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import ProgressBar from '../../components/ProgressBar';
import StatusChip from '../../components/StatusChip';
import QueueFormDialog from '../../components/QueueFormDialog';
import { useToast } from '../../components/Toast';
import { useQueues, QueueListItem, setQueueStatus } from '../../hooks/useQueues';
import { useBusinesses } from '../../hooks/useBusinesses';
import { utilPct, statusLabel } from '../../utils/format';

const QueuesPage: React.FC = () => {
  const toast = useToast();
  const { items, filters, loading, error, reload } = useQueues();
  const { items: businesses } = useBusinesses();
  const [q, setQ] = useState('');
  const [biz, setBiz] = useState('');
  const [cat, setCat] = useState('');
  const [status, setStatus] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      items.filter(
        (r) =>
          (!q || (r.name + r.business + r.id).toLowerCase().includes(q.toLowerCase())) &&
          (!biz || r.business === biz) && (!cat || r.category === cat) && (!status || r.status === status)
      ),
    [items, q, biz, cat, status]
  );

  const toggleStatus = async (r: QueueListItem) => {
    const activate = r.status !== 'active';
    setBusyId(r.id);
    try {
      await setQueueStatus(r.id, activate);
      toast(`${r.name} ${activate ? 'activated' : 'deactivated'}`, 'success');
      await reload();
    } catch (err: any) {
      toast(err?.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const columns: Column<QueueListItem>[] = [
    {
      key: 'name', label: 'Queue', csv: (r) => r.name, render: (r) => (
        <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{r.name}</Typography>
      ),
    },
    { key: 'business', label: 'Business' },
    { key: 'category', label: 'Category' },
    { key: 'providers', label: 'Providers', align: 'right' },
    { key: 'util', label: 'Utilization', csv: (r) => utilPct(r.booked, r.capacity) + '%', render: (r) => <ProgressBar booked={r.booked} capacity={r.capacity} /> },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
    {
      key: '_', label: '', render: (r) => {
        const active = r.status === 'active';
        return (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small" variant="outlined" disabled={busyId === r.id}
              onClick={() => toggleStatus(r)}
              startIcon={active ? <ToggleOffRounded /> : <ToggleOnRounded />}
              sx={{ borderColor: 'divider', color: active ? 'error.main' : 'success.main' }}
            >
              {active ? 'Deactivate' : 'Activate'}
            </Button>
            <Button component={Link} to={`/queues/${r.id}`} size="small" variant="outlined" startIcon={<VisibilityRounded />}
              sx={{ borderColor: 'divider', color: 'text.primary' }}>
              View
            </Button>
          </Box>
        );
      },
    },
  ];

  const reset = () => { setQ(''); setBiz(''); setCat(''); setStatus(''); };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error}</Alert>;
  }

  return (
    <>
      <FilterBar>
        <Field label="Search"><SearchInput value={q} onChange={setQ} placeholder="Queue or business…" /></Field>
        <Field label="Business"><SelectInput value={biz} onChange={setBiz} options={[{ value: '', label: 'All businesses' }, ...filters.businesses]} minWidth={170} /></Field>
        <Field label="Category"><SelectInput value={cat} onChange={setCat} options={[{ value: '', label: 'All categories' }, ...filters.categories]} /></Field>
        <Field label="Status"><SelectInput value={status} onChange={setStatus} options={[{ value: '', label: 'All statuses' }, ...filters.statuses.map((s) => ({ value: s, label: statusLabel(s) }))]} /></Field>
        <Spacer />
        <Field label="&nbsp;"><Button variant="contained" size="small" startIcon={<AddRounded />} onClick={() => setAddOpen(true)} sx={{ height: 40 }}>Add Queue</Button></Field>
        <ResetButton onClick={reset} />
      </FilterBar>

      <Paper sx={{ p: 0 }}>
        <DataTable columns={columns} rows={rows} title="Queues" />
      </Paper>

      <QueueFormDialog
        open={addOpen}
        mode="add"
        onClose={() => setAddOpen(false)}
        onSaved={reload}
        businesses={businesses.map((b) => ({ id: b.id, name: b.name }))}
      />
    </>
  );
};

export default QueuesPage;
